import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enviar o cookie httpOnly de autenticação em todas as requisições
axios.defaults.withCredentials = true;

// Renovação automática do access token via refresh token (cookie httpOnly)
let refreshPromise = null;
const isAuthPath = (url = '') =>
  ['/auth/login', '/auth/register', '/auth/refresh', '/auth/me', '/auth/logout'].some((p) => url.includes(p));

axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    if (status === 401 && original && !original._retry && !isAuthPath(original.url || '')) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios.post(`${API}/auth/refresh`).finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return axios(original);
      } catch (e) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Erro ao fazer logout:', error);
      }
    }
    setUser(null);
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Access token pode ter expirado: tentar renovar via refresh token
      try {
        await axios.post(`${API}/auth/refresh`);
        const retry = await axios.get(`${API}/auth/me`);
        setUser(retry.data);
      } catch (e) {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Erro ao fazer login'
      };
    }
  };

  const register = async (name, email, password, whatsapp) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password, whatsapp });
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Erro ao criar conta'
      };
    }
  };

  const value = {
    user,
    // Sentinela truthy para compatibilidade: a autenticação real ocorre via cookie httpOnly.
    // O valor não é sensível; o backend lê o cookie (que tem precedência sobre o header).
    token: user ? 'cookie-auth' : null,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
